import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserRole } from '@/types/database.types';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, 
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Copy, AlertCircle } from 'lucide-react';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
}

const addUserSchema = z.object({
  email: z.string().email('Valid email is required'),
  role: z.enum(['admin', 'finance', 'procurement', 'warehouse', 'seller', 'support', 'benchmark', 'benchmark_manager', 'coordinator', 'quality']),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  password: z.string().optional(),
});

type FormData = z.infer<typeof addUserSchema>;

export function InviteUserDialog({ open, onClose, onInviteSent }: InviteUserDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [userUpdated, setUserUpdated] = useState(false);
  
  const form = useForm<FormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      email: '',
      role: 'seller',
      firstName: '',
      lastName: '',
      password: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);
    setUserUpdated(false);

    try {
      console.log('Adding user:', data.email);
      
      // Call our Edge Function to create the user
      const { data: responseData, error: functionError } = await supabase.functions.invoke('invite-user', {
        body: {
          action: 'inviteUser',
          email: data.email,
          role: data.role,
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password || undefined
        }
      });
      
      if (functionError) {
        console.error('Error from invite-user function:', functionError);
        throw new Error(functionError.message || 'Failed to add user');
      }
      
      if (responseData.error) {
        console.error('Error response from invite-user function:', responseData.error);
        throw new Error(responseData.error);
      }
      
      // Check if a user was updated rather than created new
      if (responseData.message?.includes('already exists')) {
        setUserUpdated(true);
        toast.success('User already exists and has been updated');
        if (onInviteSent) onInviteSent();
        onClose();
        form.reset();
        return;
      }
      
      // Check if a password was generated
      if (responseData.generatedPassword) {
        setGeneratedPassword(responseData.generatedPassword);
        setShowPasswordDialog(true);
      } else {
        toast.success('User added successfully');
        if (onInviteSent) onInviteSent();
        onClose();
        form.reset();
      }
    } catch (error: any) {
      console.error('Failed to add user:', error);
      setError(error.message || 'Failed to add user');
      toast.error('Failed to add user: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setGeneratedPassword(null);
    if (onInviteSent) onInviteSent();
    onClose();
    form.reset();
  };

  const copyPasswordToClipboard = () => {
    if (generatedPassword) {
      navigator.clipboard.writeText(generatedPassword);
      toast.success('Password copied to clipboard');
    }
  };

  const roleOptions = [
    { value: 'seller', label: 'Seller', description: 'Sales operations' },
    { value: 'warehouse', label: 'Warehouse', description: 'Inventory management' },
    { value: 'finance', label: 'Finance', description: 'Financial operations' },
    { value: 'coordinator', label: 'Coordinator', description: 'Farm coordination and management' },
    { value: 'quality', label: 'Quality', description: 'Quality inspection and grading' },
    { value: 'admin', label: 'Admin', description: 'Full system access' },
    { value: 'benchmark', label: 'Benchmark', description: 'Price data entry via Telegram and web' },
    { value: 'benchmark_manager', label: 'Benchmark Manager', description: 'Monitor benchmark performance and manage team' }
  ];

  function getRoleBadgeVariant(role: UserRole) {
    switch (role) {
      case 'admin': return 'destructive';
      case 'finance': return 'success'; 
      case 'warehouse': return 'warning';
      case 'coordinator': return 'default';
      case 'quality': return 'default';
      case 'benchmark': return 'outline';
      case 'benchmark_manager': return 'default';
      default: return 'secondary';
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account. Users with benchmark roles can access the dedicated Telegram bot for data entry.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error adding user</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="user@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="John" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Doe" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" placeholder="Leave blank to auto-generate" />
                    </FormControl>
                    <FormDescription>
                      If left blank, a password will be automatically generated.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roleOptions.map((role) => (
                          <SelectItem key={role.value} value={role.value} className="flex items-center space-x-2">
                            <Badge variant={getRoleBadgeVariant(role.value as UserRole)}>
                              {role.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground ml-2">{role.description}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Choose the appropriate access level for this user. Benchmark users get access to the dedicated Telegram bot for mobile data entry.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={onClose} type="button" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding..." : "Add User"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      <AlertDialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>User Added Successfully</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-4">The user has been created with the following auto-generated password:</p>
              <div className="bg-muted p-3 rounded-md flex justify-between items-center mb-4">
                <code className="text-sm font-mono">{generatedPassword}</code>
                <Button variant="outline" size="icon" onClick={copyPasswordToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Please copy this password and share it securely with the user. 
                They will be able to log in using this password and change it after their first login.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleClosePasswordDialog}>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
